import { Component, ViewChild, Injector, OnInit } from '@angular/core';
import { RsswaSidePanelComponent } from './rsswa-side-panel/rsswa-side-panel.component';
import { RSSWAResource } from './rsswa';
import { BaseComponent } from 'src/app/common/base/base.component';
import { RsswaService, } from './rsswa.service';
import { FormStatusEnum, FormType } from 'src/app/common/enumerations/enumerations';
import { Observable, Subscription } from 'rxjs';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { CanComponentDeactivate } from 'src/app/common/guards/unsaved-changes.guard';
import { MessagePlaceholder } from 'src/app/common/models/placeholder';
import { WfSignatureResource } from '../workflow/workflow';
import { FormViewState } from 'src/app/common/models/form-view-state';
import { FormHistoryButtonComponent } from 'src/app/controls/form-history/form-history-button/form-history-button.component';
import { RsswaMainPanelComponent } from './rsswa-main-panel/rsswa-main-panel.component';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

@Component({
  selector: 'rsswa',
  templateUrl: './rsswa.component.html',
  styleUrls: ['./rsswa.component.scss']
})

export class RsswaComponent extends BaseComponent implements OnInit, CanComponentDeactivate {

  filter: any;
  valueFilter!: string;

  rsswa?: RSSWAResource | null;
  rsswas?: RSSWAResource[];
  rsswasFiltered?: RSSWAResource[];
  rsswas$!: Observable<RSSWAResource[]>;
  rsswasSubs!: Subscription;

  status = new FormViewState();

  canCreateAmendment!: boolean | null;
  wfAmendmentSignature?: WfSignatureResource;
  showAmendmentToggle = false;
  testingCompleteSigned? = false;

  @ViewChild(RsswaSidePanelComponent) public sidePanel!: RsswaSidePanelComponent;
  @ViewChild(FormHistoryButtonComponent) public showHistory!: FormHistoryButtonComponent;
  @ViewChild(RsswaMainPanelComponent) public mainPanel!: RsswaMainPanelComponent;

  constructor(
    protected override injector: Injector,
    public service: RsswaService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.loadRSSWAs();
  }

  ngAfterViewInit(): void {
    this.setHeights();
  }

  applyFilter(e: any) {
    if (!e)
      this.valueFilter = '';
    this.filter = e;
  }

  loadRSSWAs() {
    this.rsswas$ = this.store.select(state => state.RSSWA.data);
    this.rsswasSubs = this.rsswas$.subscribe(data => {
      if (data?.length) {
        this.rsswas = data;
        if (this.rsswa?.serialNo) {
          const status = this.rsswa?.status;
          const rsswa = this.rsswas.find(r => r.serialNo == this.rsswa?.serialNo);
          if (rsswa) {
            this.rsswa = rsswa;
            this.sidePanel.selectedRowIndex = this.rsswa.id;
            this.checkPrivileges();
            if (this.rsswa.status != status) this.alert.message('formStateChanged', [new MessagePlaceholder('{serialNo}', this.rsswa?.serialNo), new MessagePlaceholder('{status}', this.rsswa.statusName)], undefined, 'Form Status Changed');
            if (this.rsswa.status == FormStatusEnum.Active) this.status.isViewingAmendment = false;
          } else {
            this.rsswa = null;
            this.sidePanel.selectedRowIndex = -1;
          };
        }
      }
    });
  }

  checkPrivileges() {
    const signatures = this.rsswa?.wfTable?.wfTableLocal?.wfSectionLocals.map(s => s.wfSignatures)?.flatMap(a => a || []);
    this.wfAmendmentSignature = this.rsswa?.wfTable?.wfTableLocal?.wfSectionLocals.find(s => s.number == 9)?.wfSignatures?.[0];
    this.canCreateAmendment = this.rsswa?.status == FormStatusEnum.Active && this.hasPrivilege(this.privilegeEnum.RequestRSSWAAmendment) && this.wfAmendmentSignature != null;
    this.showAmendmentToggle = this.rsswa?.status === FormStatusEnum.AmendmentDrafting || this.rsswa?.status === FormStatusEnum.AmendmentApprovals;
    this.testingCompleteSigned = signatures?.find(s => s.code == 'TESTING_COMPLETE')?.signed;
    this.setHeights();
  }

  async create() {
    if (await this.canDeactivate())
      if (this.sidePanel) {
        const serialNo = await this.service.getSerialNo().toPromise()
        this.sidePanel.selectedRowIndex = -1;
        this.rsswa = {
          id: 0,
          type: FormType.RSSWA,
          serialNo: serialNo?.serialNo,
          status: FormStatusEnum.New,
          statusName: 'New',
          createdBy: this.getCurrentUser()?.id,
          createdByName: this.getCurrentUser()?.name,
          createdOn: new Date(),
          formStatus: { name: 'New', color: 'yellow' },
          editingBy: this.getCurrentUser(),
          editingById: this.getCurrentUser()?.id,
          startOn: new Date(),
          completeOn: new Date()
        } as RSSWAResource;
        this.rsswa = (await this.service.create(this.rsswa).toPromise()) as RSSWAResource;
        this.sidePanel.selectedRowIndex = this.rsswa.id;
        this.showAmendmentToggle = false;
        this.status.isViewingAmendment = false;
        this.setFormDirty();
        this.setOpenDocument(FormType.RSSWA, this.rsswa.id);
      }
  }

  async edit(value: boolean) {
    if (!value && !this.canReleaseEditingForm()) return;

    if (value) {
      this.changeEditing(value);
    }
    else if (!value && await this.canDeactivate()) {
      const confirm = this.dialog.open(YesNoDialogComponent, {
        width: "500px", data: {
          message: this.getMessage("SCF_Release_confirmation")
            ?.description, icon: "stop",
        },
      });
      confirm.afterClosed().subscribe(async data => {
        if (data) {
          this.changeEditing(value);
        }
      });
    }
  }

  canReleaseEditingForm(): boolean {
    const isReleaseStatus =
      this.rsswa?.status == FormStatusEnum.Draft ||
      this.rsswa?.status == FormStatusEnum.AmendmentDrafting;
    const isCurrentEditor = this.rsswa?.editingById == this.currentUser?.id;

    return !!(
      this.rsswa?.editingBy &&
      isReleaseStatus &&
      (isCurrentEditor || this.hasPrivilege(this.privilegeEnum.ReleaseEditingForms))
    );
  }

  changeEditing(value: boolean) {
    if (this.rsswa?.id) {
      this.service.setEditing(this.rsswa?.id, value).toPromise();
      if (this.rsswa?.status == this.formStatusEnum.AmendmentDrafting)
        this.status.isViewingAmendment = value;
    }
  }

  requestAmendment() {
    const confirm = this.dialog.open(YesNoDialogComponent, {
      width: '500px',
      data: {
        message: this.getMessage('request_amendment')?.description,
        icon: 'warn'
      }
    });

    confirm.afterClosed().subscribe(async data => {
      if (data && this.rsswa) {
        await this.service.requestAmendment(this.rsswa.id).toPromise();
        this.status.isViewingAmendment = true;
        setTimeout(() => {
          this.rowSelected(this.rsswa);
        }, 1000);
      }
    });
  }

  /**
   * Confirms unsaved changes before switching views and restores the toggle when canceled.
   * @param e Toggle event containing the requested amendment view state.
   */
  async showAmendmentChange(e: MatSlideToggleChange) {
    const currentValue = !!this.status.isViewingAmendment;
    const preservePendingChanges = currentValue && this.isFormDirty();
    if (await this.canDeactivate()) {
      // Snapshot unsaved amendment values before rebuilding the requested view.
      if (preservePendingChanges)
        this.mainPanel?.preservePendingAmendmentChanges();
      this.status = { ...this.status, isViewingAmendment: e.checked };
      if (preservePendingChanges)
        this.setFormDirty();
    } else {
      // Material changes its internal state before the asynchronous guard resolves.
      e.source.checked = currentValue;
    }
  }

  changedEvent(e: RSSWAResource | null) {
    this.rsswa = e;
  }

  rowSelected(e: any) {
    if (e?.id != this.rsswa?.id) this.scrollToTop('sidenav-content');
    if (!e) this.showHistory.value = false;
    this.rsswa = e;
    this.status.isViewingAmendment = (this.rsswa?.status == FormStatusEnum.AmendmentDrafting || this.rsswa?.status == FormStatusEnum.AmendmentApprovals) && this.rsswa?.editingById == this.getCurrentUser()?.id;
    this.loadRSSWAs();
  }

  duplicate() {
    const removeRef = this.dialog.open(YesNoDialogComponent, {
      width: '20em',
      data: {
        message: 'Are you sure you want to copy this form?',
        icon: 'warn',
        val: false
      }
    });
    removeRef.afterClosed().subscribe(result => {
      if (result && this.rsswa) {
        this.service.copyRSSWA(this.rsswa.id, false).subscribe(data => {
          this.rsswa = data;
          this.setOpenDocument(FormType.RSSWA, this.rsswa.id);
          this.sidePanel.loadRSSWAs();
          this.sidePanel.selectedRowIndex = this.rsswa.id;
          this.showAmendmentToggle = false;
          this.status.isViewingAmendment = false;
          this.setOpenDocument(FormType.RSSWA, this.rsswa.id);
        });
      }
    });
  }

}
